<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { peopleApi, dealsApi, activitiesApi } from '$lib/api/crm';
  import { onDestroy } from 'svelte';
  import { confirmAction } from '$lib/state/actionDialog.svelte';
  import { createTouchpointDeleter, deletableTouchpoint } from '$lib/utils/deleteTouchpoint';
  import { apiFetch, getErrorMessage } from '$lib/api/client';
  import { touchpointsApi } from '$lib/api/touchpoints';
  import { session } from '$lib/state/session.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { istInput, istISO, dayOffset, outcomeTags, defaultAttendee, defaultLogAttendee, draftKey, editableTouchpoint, touchpointClock, touchpointTypeLabel, outcomeLabel, istTime } from '$lib/utils/touchpoints';
  import { relationshipLabels } from '$lib/utils/salesHome';
  import type { Person, Activity } from '$lib/types';
  import Modal from '$lib/components/ui/Modal.svelte';
  import CustomFieldsForm from '$lib/components/ui/CustomFieldsForm.svelte';

  let { person = null, personId, verticalId, companyId, dealId, activity = null, mode = 'log', initialType, onSaved = () => {}, onClose = () => {} }: {
    person?: Person | null; personId?: number; verticalId?: number; companyId?: number | null; dealId?: number; activity?: Activity | null;
    mode?: 'log' | 'plan'; initialType?: string; onSaved?: () => void; onClose?: () => void;
  } = $props();

  const client = useQueryClient();
  let selected = $state<Person | null>(null), search = $state(''), error = $state(''), busy = $state(false);
  let form = $state({ mode: 'log', type: 'call', when: istInput(), text: '', tags: [] as string[], next: '', date: '', none: false, attendee: null as number | null, location: '', deal: null as number | null });
  let loaded = $state(''), attendeeChosen = $state(false), planDetails = $state(false);
  let custom = $state<Record<string, unknown>>({}), dealSearch = $state('');
  let deleteBusy = $state(false), deletedDraft = $state('');
  let active = true;
  onDestroy(() => { active = false; });
  const deleteLog = createTouchpointDeleter({
    confirm: confirmAction, remove: activitiesApi.remove, invalidate: () => client.invalidateQueries(),
    removeDraft: key => localStorage.removeItem(key), setBusy: value => { deleteBusy = value; },
    setError: err => { error = err ? `${getErrorMessage(err)} Draft kept. You can retry Delete.` : ''; }
  });
  function removeLog() {
    if (busy || deleteBusy || !activity) return;
    const target = activity, targetId = activity.id, requestDraft = loaded, contactId = id, closeEditor = onClose, userId = session.me?.user.id;
    return deleteLog(target, session.me?.user.id ?? 0, {
      isCurrent: () => active && activity === target && activity.id === targetId && id === contactId && loaded === requestDraft && onClose === closeEditor && session.me?.user.id === userId,
      onDeleted: () => {
        deletedDraft = requestDraft;
        try { if (requestDraft) localStorage.removeItem(requestDraft); } catch { /* Storage may be unavailable. */ }
        closeEditor();
      }
    });
  }

  const id = $derived(selected?.id ?? person?.id ?? personId ?? activity?.person_id ?? undefined);
  const detail = createQuery(() => ({ queryKey: ['person', id], queryFn: () => peopleApi.get(id!), enabled: !!id && !person && !selected }));
  const contact = $derived(selected ?? person ?? detail.data);
  const pipeline = $derived(contact?.vertical_id ?? verticalId ?? activity?.vertical_id);
  const people = createQuery(() => ({
    queryKey: ['composer-people', search, companyId, verticalId, session.selectedVertical],
    queryFn: () => peopleApi.list({ q: search, company_id: companyId ?? undefined, vertical_id: verticalId ?? (session.selectedVertical === 'all' ? undefined : session.selectedVertical), per_page: 15 }),
    enabled: !id && search.length >= 2
  }));
  const assignees = createQuery(() => ({
    queryKey: ['activity-assignees', pipeline],
    queryFn: () => apiFetch<Array<{ id: number; name: string }>>(`/activities/assignees?vertical_id=${pipeline}`),
    enabled: !!pipeline
  }));
  const deals = createQuery(() => ({
    queryKey: ['composer-deals', id, pipeline, dealSearch],
    queryFn: () => dealsApi.list({ person_id: id, vertical_id: pipeline, q: dealSearch, per_page: 25 }),
    enabled: !!id && !!pipeline
  }));
  const clashes = createQuery(() => ({
    queryKey: ['composer-clashes', form.attendee, form.when.slice(0, 10), pipeline],
    queryFn: () => touchpointsApi.list({ view: 'calendar', assignee_ids: form.attendee!, from: form.when.slice(0, 10), to: form.when.slice(0, 10), vertical_id: pipeline, status: 'scheduled' }),
    enabled: form.mode === 'plan' && !!form.attendee && !!pipeline
  }));

  // Load the form for this contact/record once, then restore any local draft.
  $effect(() => {
    if (!id) return;
    const key = draftKey(session.me?.user.id ?? 0, id, activity?.id) + (activity ? `:${mode}` : '');
    if (loaded === key) return;
    loaded = key;
    const completing = activity?.status === 'scheduled' && mode === 'log';
    const logWhen = activity?.status === 'completed' ? touchpointClock(activity) : new Date().toISOString();
    form = {
      mode,
      type: initialType === 'email' ? 'note' : initialType ?? activity?.type ?? (mode === 'plan' ? 'meeting' : 'call'),
      when: istInput(mode === 'plan' ? (activity?.scheduled_at ?? new Date(Date.now() + 3600000).toISOString()) : logWhen),
      text: activity?.body || activity?.title || '',
      tags: activity?.outcome_tags ?? [],
      next: completing ? '' : activity?.next_action ?? '',
      date: completing ? '' : activity?.next_action_date?.slice(0, 10) ?? '',
      none: completing ? false : activity?.no_followup ?? false,
      attendee: activity?.assignee_user_id ?? null,
      location: activity?.location ?? '',
      deal: activity?.deal_id ?? null
    };
    custom = { ...activity?.custom };
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        form = { ...form, ...parsed };
        custom = parsed._custom ?? custom;
        attendeeChosen = !!parsed._attendeeChosen;
      }
    } catch { /* Keep in-memory draft. */ }
    if (dealId && !activity) form.deal = dealId;
    if (activity?.status === 'scheduled' && mode === 'plan' && !activity.scheduled_at && activity.due_at) form.when = istInput(activity.due_at);
  });

  // Default attendee until the user picks one. A plan defaults to the contact
  // owner (a coordinator never books herself by default); a log defaults to
  // the person recording what they did, never to the owner.
  $effect(() => {
    if (!contact || attendeeChosen || activity || !assignees.data) return;
    const eligible = assignees.data.map(u => u.id);
    const wanted = form.mode === 'plan'
      ? defaultAttendee(contact.owner_user_id, session.me?.user.id, !!session.me?.user.intake_coordinator, eligible)
      : defaultLogAttendee(session.me?.user.id, eligible);
    if (form.attendee !== wanted) form.attendee = wanted;
  });

  $effect(() => {
    if (loaded && !busy && !deleteBusy && loaded !== deletedDraft) {
      try { localStorage.setItem(loaded, JSON.stringify({ ...form, _custom: custom, _attendeeChosen: attendeeChosen })); } catch { /* In-memory draft remains. */ }
    }
  });

  function tag(value: string) {
    form.tags = form.tags.includes(value) ? form.tags.filter(t => t !== value) : [...form.tags, value];
    if (value === 'quote_requested' && !form.next) { form.next = 'Send quote'; form.date = dayOffset(2); form.none = false; }
  }
  function earlierToday() {
    const now = istInput();
    const start = Date.parse(`${now.slice(0, 10)}T00:00:00+05:30`);
    form.when = istInput(new Date(Math.max(start, Date.now() - 3600000)).toISOString());
  }
  function switchMode(next: 'log' | 'plan') {
    if (form.mode === next) return;
    form.mode = next;
    if (next === 'plan') {
      form.type = 'meeting';
      form.when = istInput(new Date(Date.now() + 3600000).toISOString());
    } else {
      form.when = istInput(activity?.status === 'completed' ? touchpointClock(activity) : new Date().toISOString());
    }
  }
  function validate(): boolean {
    error = '';
    if (!contact && detail.isError) error = 'Could not load this contact. Retry the contact lookup before saving.';
    else if (!id || !pipeline || !contact) error = 'Choose a contact first.';
    else if (companyId != null && contact.company_id !== companyId && contact.hotel_company_id !== companyId) error = 'Choose a contact linked to this company.';
    else if (companyId != null && verticalId != null && contact.vertical_id !== verticalId) error = 'Choose a contact in this company’s pipeline.';
    else if (activity && !editableTouchpoint(activity)) error = 'Synced emails are read-only.';
    else if (activity && activity.status !== 'scheduled' && form.mode === 'plan') error = 'A recorded touchpoint cannot become a new plan. Close this editor and create a plan instead.';
    else if (form.mode === 'plan' && (assignees.isError || !form.attendee || !assignees.data?.some(u => u.id === form.attendee))) error = 'Choose an eligible attendee for Who’s going.';
    else if (activity?.status === 'scheduled' && form.mode === 'log' && activity.assignee_user_id !== session.me?.user.id) error = 'Only the current attendee can complete this plan.';
    else if (activity?.status === 'scheduled' && form.mode === 'log' && Date.parse(activity.scheduled_at ?? '') > Date.now()) error = 'This appointment is still in the future. Reschedule it or wait until it starts.';
    else if (form.mode === 'plan' && !planDetails && (!form.when || Date.parse(istISO(form.when)) <= Date.now())) error = 'Choose a future time in IST.';
    else if (form.mode === 'log' && (!form.when || Date.parse(istISO(form.when)) > Date.now())) error = 'Choose when this actually happened, not a future time.';
    else if (form.mode === 'log' && !form.text.trim()) error = 'What happened? Add one sentence.';
    else if (form.mode === 'log' && !form.none && (!form.next.trim() || !form.date)) error = 'Add a next step and date, or choose No follow-up needed.';
    return !error;
  }
  /** Saves straight away: closing or navigating never silently drops a save. */
  function submit() {
    if (busy || deleteBusy || loaded === deletedDraft || !validate()) return;
    void save();
  }
  function close() {
    if (busy || deleteBusy) return;
    onClose();
  }
  async function save() {
    if (busy) return;
    busy = true;
    const requestDraft = loaded;
    const planning = form.mode === 'plan';
    const unchangedOccurrence = activity?.status === 'completed' && form.when === istInput(touchpointClock(activity)) ? touchpointClock(activity) : undefined;
    const originalPlan = activity?.scheduled_at ?? activity?.due_at;
    const plannedAt = planning && originalPlan && form.when === istInput(originalPlan) ? originalPlan : planning ? istISO(form.when) : activity?.scheduled_at ?? null;
    const body = {
      person_id: id, vertical_id: pipeline, company_id: companyId ?? contact?.company_id ?? null, type: form.type,
      title: form.text.trim().split('\n')[0] || `${touchpointTypeLabel(form.type)} with ${contact?.first_name ?? 'contact'}`,
      body: form.text.trim(), outcome_tags: form.tags, no_followup: planning ? false : form.none,
      next_action: form.none ? '' : form.next.trim(), next_action_date: form.none || !form.date ? null : `${form.date}T00:00:00Z`,
      occurred_at: planning ? null : istISO(form.when), scheduled_at: plannedAt, assignee_user_id: form.attendee,
      location: form.location, deal_id: form.deal, status: planning ? 'scheduled' : 'completed'
    };
    try {
      if (activity?.status === 'scheduled' && !planning) {
        await touchpointsApi.complete(activity.id, { what_happened: body.body, outcome_tags: body.outcome_tags, no_followup: body.no_followup, next_action: body.next_action, next_action_date: body.next_action_date, occurred_at: body.occurred_at });
      } else if (activity?.status === 'scheduled' && planning && planDetails) {
        await touchpointsApi.write(`/activities/${activity.id}`, { title: body.title, body: body.body, assignee_user_id: body.assignee_user_id, location: body.location, deal_id: body.deal_id }, 'PUT');
      } else if (activity?.status === 'scheduled' && planning) {
        await touchpointsApi.reschedule(activity.id, activity.scheduled_at ? { scheduled_at: body.scheduled_at } : { due_at: body.scheduled_at });
      } else {
        await touchpointsApi.write(activity ? `/activities/${activity.id}` : '/activities', {
          ...body,
          ...(!planning && unchangedOccurrence ? { occurred_at: unchangedOccurrence } : {}),
          custom,
          status: activity?.status ?? body.status,
          ...(!planning ? { what_happened: body.body } : {})
        }, activity ? 'PUT' : 'POST');
      }
      try { localStorage.removeItem(requestDraft); } catch { /* No storage. */ }
      await client.invalidateQueries();
      toast.success(planning ? 'Plan saved.' : 'Touchpoint saved.');
      onSaved();
      onClose();
    } catch (err) {
      error = `${getErrorMessage(err)} Draft kept. Check the timeline before retrying if the connection dropped.`;
    } finally {
      busy = false;
    }
  }
  function keyboard(e: KeyboardEvent) {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); submit(); }
  }
</script>

<svelte:window onkeydown={keyboard} />
<Modal title={activity ? (activity.status === 'scheduled' ? (form.mode === 'log' ? `Log outcome · planned ${touchpointTypeLabel(activity.type).toLowerCase()}` : `Planned ${touchpointTypeLabel(activity.type).toLowerCase()}`) : 'Edit touchpoint') : form.mode === 'plan' ? 'Plan a touchpoint' : 'Log a touchpoint'} onClose={close} wide>
  <div class="composer">
    {#if !id}
      <label class="field">Find a contact
        <input class="input" type="search" bind:value={search} placeholder="Name, phone or email (2+ characters)" />
      </label>
      {#if people.isError}<p class="tym-error-state" role="alert">{getErrorMessage(people.error)}</p>{/if}
      <div class="picker">
        {#each people.data?.results ?? [] as p (p.id)}
          <button type="button" class="pick" onclick={() => selected = p}>
            <strong>{p.first_name} {p.last_name}</strong>
            <span>{[p.company_name, p.city].filter(Boolean).join(' · ') || relationshipLabels[p.relationship_type ?? ''] || 'Contact'}</span>
          </button>
        {/each}
      </div>
    {:else if !person && !selected && detail.isError}
      <p class="tym-error-state" role="alert">Could not load this contact: {getErrorMessage(detail.error)}</p>
      <div class="row-actions">
        <button type="button" class="btn-secondary" onclick={() => detail.refetch()}>Retry contact</button>
        <button type="button" class="btn-secondary" onclick={close}>Close (draft kept)</button>
      </div>
    {:else if !contact && detail.isPending}
      <p class="tym-loading-state" role="status">Loading contact…</p>
    {:else if activity?.type === 'email'}
      <p class="hint">Synced email · read-only. Open it from the timeline using your email access.</p>
    {:else}
      <div class="who">
        <div>
          <strong>{contact?.first_name} {contact?.last_name}</strong>
          <span>{[contact?.company_name, relationshipLabels[contact?.relationship_type ?? '']].filter(Boolean).join(' · ')}</span>
        </div>
        <span class="day-chip {contact?.owner_user_id ? 'day-chip-info' : 'day-chip-warn'}">Owner: {contact?.contact_owner_name ?? 'No owner'}</span>
      </div>

      {#if activity?.status === 'scheduled' && form.mode === 'plan'}
        <fieldset disabled={busy}>
          <div class="plan-summary">
            <p class="eyebrow">{planDetails ? 'Edit plan details' : 'Reschedule'}</p>
            <strong>{activity.title || touchpointTypeLabel(activity.type)}</strong>
            <p class="hint">
              Who's going: {activity.assignee_name ?? (activity.assignee_user_id ? 'Former team member' : 'Unassigned')}
              · Booked by {activity.booked_by_name || activity.created_by}
            </p>
          </div>
          {#if planDetails}
            <label class="field">Agenda<textarea class="input" rows="3" bind:value={form.text}></textarea></label>
            <label class="field">Who's going
              <select class="input" bind:value={form.attendee} onchange={() => attendeeChosen = true}>
                <option value={null}>Choose eligible attendee</option>
                {#each assignees.data ?? [] as user (user.id)}<option value={user.id}>{user.name}</option>{/each}
              </select>
            </label>
            <label class="field">Where<input class="input" bind:value={form.location} /></label>
            <p class="hint">Date and time stay unchanged. To move it, use Reschedule.</p>
          {:else}
            <label class="field">New date and time (IST)<input class="input" type="datetime-local" bind:value={form.when} /></label>
            <p class="hint">Rescheduling changes only the date and time.</p>
          {/if}
          <div class="row-actions">
            <button type="button" class="day-mini-btn" onclick={() => { planDetails = !planDetails; }}>{planDetails ? 'Reschedule instead' : 'Edit agenda or attendee'}</button>
            <button type="button" class="day-mini-btn" onclick={() => { form.mode = 'log'; form.when = istInput(); form.next = ''; form.date = ''; form.none = false; }}>Done — log what happened</button>
          </div>
        </fieldset>
      {:else}
        <fieldset disabled={busy || deleteBusy} class:log-mode={form.mode === 'log'}>
          {#if activity?.status === 'scheduled'}
            <p class="plan-summary">Completing the planned {touchpointTypeLabel(activity.type).toLowerCase()} “{activity.title}”. Attendee and linked records stay unchanged.</p>
          {:else}
            <div class="day-segment" role="group" aria-label="Log or plan">
              <button type="button" aria-pressed={form.mode === 'log'} onclick={() => switchMode('log')}>Log what happened</button>
              <button type="button" aria-pressed={form.mode === 'plan'} onclick={() => switchMode('plan')}>Plan something</button>
            </div>
            <div class="chips" role="group" aria-label="Type">
              {#each form.mode === 'plan' ? ['call', 'meeting', 'visit', 'task'] : ['call', 'meeting', 'whatsapp', 'visit', 'note'] as type}
                <button type="button" class="tym-chip" class:active={form.type === type} onclick={() => form.type = type}>{touchpointTypeLabel(type)}</button>
              {/each}
            </div>
          {/if}
          {#if form.mode === 'log' && activity?.status !== 'scheduled'}
            <p class="hint email-hint">Emails sync automatically. Use Note for anything else you want on record.{#if contact?.email} <a class="day-link" href={`mailto:${contact.email}`}>Write an email ↗</a>{/if}</p>
          {/if}

          <div class="grid-2 f-when">
            <label class="field">{form.mode === 'log' ? 'When did it happen? (IST)' : 'When (IST)'}
              <input class="input" type="datetime-local" bind:value={form.when} />
            </label>
            {#if form.mode === 'log'}
              <div class="quick">
                <button type="button" class="day-mini-btn" onclick={() => form.when = istInput()}>Just now</button>
                <button type="button" class="day-mini-btn" onclick={earlierToday}>Earlier today</button>
                <button type="button" class="day-mini-btn" onclick={() => form.when = istInput(new Date(Date.now() - 86400000).toISOString())}>Yesterday</button>
              </div>
            {:else}
              <label class="field">Where<input class="input" bind:value={form.location} placeholder="Site, office or phone" /></label>
            {/if}
          </div>

          <label class="field f-text">{form.mode === 'log' ? 'What happened' : 'Agenda (optional)'}
            <textarea class="input" rows="3" bind:value={form.text} placeholder={form.mode === 'log' ? 'One sentence is enough' : 'What should be covered?'}></textarea>
          </label>

          {#if form.mode === 'log'}
            <div class="field f-outcome">
              <span>{form.mode === 'log' ? 'How did it go?' : 'Outcome'}</span>
              <div class="chips">
                {#each outcomeTags as value}
                  <button type="button" class="tym-chip" class:active={form.tags.includes(value)} onclick={() => tag(value)}>{outcomeLabel(value)}</button>
                {/each}
              </div>
            </div>
            <div class="next-step f-next" class:muted={form.none}>
              <label class="field">Next step<input class="input" bind:value={form.next} disabled={form.none} placeholder="e.g. Send revised quote" /></label>
              <div class="quick">
                {#each [[1, 'Tomorrow'], [3, 'In 3 days'], [7, 'Next week']] as [days, text]}
                  <button type="button" class="day-mini-btn" class:chosen={form.date === dayOffset(Number(days)) && !form.none} onclick={() => { form.date = dayOffset(Number(days)); form.none = false; }}>{text}</button>
                {/each}
                <input class="input date" aria-label="Next step date" type="date" bind:value={form.date} disabled={form.none} />
              </div>
              <label class="check"><input type="checkbox" bind:checked={form.none} /> No follow-up needed</label>
            </div>
          {/if}

          <label class="field f-who">{form.mode === 'plan' ? "Who's going" : 'Handled by (optional)'}
            <select class="input" bind:value={form.attendee} disabled={activity?.status === 'scheduled'} onchange={() => attendeeChosen = true}>
              <option value={null}>{form.mode === 'plan' ? 'Choose attendee' : 'Not recorded'}</option>
              {#each assignees.data ?? [] as user (user.id)}<option value={user.id}>{user.name}</option>{/each}
            </select>
          </label>
          {#if assignees.isError}<p class="tym-error-state" role="alert">{getErrorMessage(assignees.error)}</p>{/if}

          {#if form.mode === 'plan'}
            <details class="more">
              <summary>Attendee's other plans that day{clashes.data ? ` (${clashes.data.count})` : ''}</summary>
              {#if clashes.isError}<p class="hint">Could not check clashes. Verify availability before booking.</p>{/if}
              {#each clashes.data?.results ?? [] as row (row.id)}
                <p class="hint">{istTime(row.scheduled_at ?? row.due_at ?? row.created_at)} · {row.title}</p>
              {:else}{#if clashes.data}<p class="hint">Nothing else planned.</p>{/if}{/each}
            </details>
            <p class="hint">CRM plan only — no calendar invitation is sent.</p>
          {/if}

          {#if activity?.status !== 'scheduled'}
            <details class="more f-more">
              <summary>Link a deal and more</summary>
              <label class="field">Find a deal (this contact only)<input class="input" type="search" bind:value={dealSearch} /></label>
              <label class="field">Linked deal
                <select class="input" bind:value={form.deal}>
                  <option value={null}>None</option>
                  {#if form.deal && !deals.data?.results.some(deal => deal.id === form.deal)}<option value={form.deal}>Linked deal #{form.deal}</option>{/if}
                  {#each deals.data?.results ?? [] as deal (deal.id)}<option value={deal.id}>{deal.name}</option>{/each}
                </select>
              </label>
              {#if deals.isError}<p class="tym-error-state" role="alert">{getErrorMessage(deals.error)}</p>{/if}
              {#if deals.data && deals.data.count > deals.data.results.length}<p class="hint">Showing {deals.data.results.length} of {deals.data.count} matches. Refine the search.</p>{/if}
              <CustomFieldsForm defs={session.customFieldsFor('activities', pipeline)} values={custom} />
              <p class="hint">{companyId != null ? 'Contact must belong to this company and pipeline.' : 'Pipeline and company follow the contact.'} Booked by {activity?.booked_by_name || activity?.created_by || session.me?.user.name}.</p>
            </details>
          {/if}
        </fieldset>
      {/if}

      {#if error}<p role="alert" class="tym-error-state">{error}</p>{/if}
      <div class="footer">
        <span class="hint">Ctrl/⌘ + Enter to save · drafts are kept on this device</span>
        <div class="row-actions">
          {#if deletableTouchpoint(activity)}<button type="button" class="btn-secondary" disabled={busy || deleteBusy} onclick={removeLog}>{deleteBusy ? 'Deleting…' : 'Delete'}</button>{/if}
          <button type="button" class="btn-secondary" disabled={busy || deleteBusy} onclick={close}>Close</button>
          <button type="button" class="btn-primary" disabled={busy || deleteBusy} onclick={submit}>{busy ? 'Saving…' : form.mode === 'plan' && !(activity?.status === 'scheduled') ? 'Save plan' : 'Save touchpoint'}</button>
        </div>
      </div>
    {/if}
  </div>
</Modal>

<style>
  .composer, fieldset { display: grid; gap: 16px; }
  fieldset { border: 0; padding: 0; margin: 0; min-width: 0; }
  .field { display: grid; gap: 6px; font-size: 13px; font-weight: 600; color: var(--tym-navy); }
  .hint { font-size: 12px; color: var(--tym-dark-gray); }
  .eyebrow { font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--tym-teal); }
  .chips, .quick, .row-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
  .grid-2 { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 12px; align-items: end; }
  .who { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 12px 14px; border-radius: var(--tym-radius-md); background: var(--tym-light-carbon); }
  .who strong { display: block; font-family: var(--tym-font-display); color: var(--tym-navy); font-size: 16px; }
  .who span:not(.day-chip) { font-size: 12px; color: var(--tym-dark-gray); }
  .plan-summary { display: grid; gap: 4px; padding: 12px 14px; border-radius: var(--tym-radius-md); background: var(--tym-teal-100); color: var(--tym-navy); font-size: 13px; }
  .next-step { display: grid; gap: 10px; padding: 14px; border-radius: var(--tym-radius-md); border: 1px solid var(--tym-cloudy-gray); }
  .next-step.muted { background: var(--tym-light-carbon); }
  .date { width: auto; min-width: 150px; }
  .chosen { background: var(--tym-navy); border-color: var(--tym-navy); color: var(--tym-white); }
  .chosen:hover:not(:disabled) { background: var(--tym-navy); }
  .check { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; color: var(--tym-heavy-carbon); min-height: 36px; }
  .check input { width: 18px; height: 18px; accent-color: var(--tym-teal); }
  .more { border-top: 1px solid var(--tym-cloudy-gray); padding-top: 4px; display: grid; gap: 10px; }
  .more summary { cursor: pointer; min-height: 40px; display: flex; align-items: center; font-size: 13px; font-weight: 600; color: var(--tym-teal); }
  .picker { display: grid; gap: 6px; }
  .pick { display: grid; gap: 2px; text-align: left; padding: 10px 12px; border-radius: var(--tym-radius-sm); border: 1px solid var(--tym-cloudy-gray); background: var(--tym-white); }
  .pick:hover { background: var(--tym-teal-100); }
  .pick strong { color: var(--tym-navy); font-size: 14px; }
  .pick span { font-size: 12px; color: var(--tym-dark-gray); }
  .footer { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; border-top: 1px solid var(--tym-cloudy-gray); padding-top: 14px; }
  @media (max-width: 640px) {
    .composer, fieldset { gap: 14px; }
    .grid-2 { grid-template-columns: 1fr; }
    .who { padding: 10px 12px; }
    .chips { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; margin-right: -20px; padding-right: 20px; }
    .chips::-webkit-scrollbar { display: none; }
    .chips .tym-chip { flex: none; min-height: 40px; }
    .email-hint { display: none; }
    /* Logging on a phone: answer "how did it go" first; the time defaults to now. */
    .log-mode .f-outcome { order: 1; }
    .log-mode .f-text { order: 2; }
    .log-mode .f-next { order: 3; }
    .log-mode .f-when { order: 4; }
    .log-mode .f-who { order: 5; }
    .log-mode .f-more { order: 6; }
    .f-outcome .chips { flex-wrap: wrap; overflow: visible; margin-right: 0; padding-right: 0; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
    .f-outcome .tym-chip { min-height: 44px; justify-content: center; border-radius: 12px; }
    .next-step { padding: 12px; }
    /* Pinned over the sheet body's own bottom padding so nothing scrolls out beneath it. */
    .footer { position: sticky; bottom: calc(-20px - env(safe-area-inset-bottom)); z-index: 2; margin: 0 -20px calc(-20px - env(safe-area-inset-bottom)); padding: 12px 20px calc(12px + env(safe-area-inset-bottom)); background: var(--tym-white); }
    .footer .hint { display: none; }
    .footer .row-actions { width: 100%; flex-wrap: nowrap; }
    .footer .row-actions .btn-secondary { flex: 0 0 auto; }
    .footer .row-actions .btn-primary { flex: 1; min-height: 48px; }
  }
</style>
